import React, { useEffect, useRef, useState } from 'react';
import { Button, Input, Space, Typography } from 'antd';
import _ from 'lodash';
import moment from 'moment';
import { toast } from 'react-toastify';

import { useCreateTicket } from './store';
import { addCustomerAddress } from 'modules/dashboards/actionables_v2/api';
import { useAddressStore } from 'modules/dashboards/actionables_v2/store';
import {
    SLOT_TYPES,
    HAIR_COACH_ISSUES,
    SLOT_NOT_CATERED,
    SLOT_NOT_CATERED_RESOLUTION,
    SLOT_NOT_CATERED_CLOSING_NOTE,
} from './constants';
import { slotApi } from 'modules/dashboards/actionables/api';
import {
    getFormattedDate,
    noSlotInMedicalEscalation,
} from 'modules/utilities/utility';
import useAssignedStore from 'modules/my_tickets/assigned_tickets/store';
import { CATEGORY } from 'modules/my_tickets/data';

const { Text } = Typography;

// Values mirror productChangePaymentOptions in utilities, which feeds the payment-option dropdown.
const PAYMENT_OPTIONS = {
    PREPAID: 'PREPAID',
};

// CLOSED in the ticketing service's status codes.
const TICKET_STATUS_CLOSED = 5;

// Which self-closing flow ran to completion, so the caller can shut the window. Both of these close
// their ticket the moment they raise it, leaving nothing further to submit.
const COMPLETED_FLOW = {
    PRODUCT_CHANGE: 'product_change',
    SLOT_NOT_CATERED: 'slot_not_catered',
};

// The agent's typed reason is the only thing on the ticket explaining WHY the change was made, so the
// order details are appended to it rather than replacing it.
const withReplacementSummary = (payload, replacementSummary) => {
    if (!replacementSummary) return payload;
    const description = _.get(payload, ['comment', 'description'], '');
    return {
        ...payload,
        comment: {
            ...payload.comment,
            description: description ? `${description}\r\n\r\n${replacementSummary}` : replacementSummary,
        },
    };
};

const CreateTicketFooter = ({
    ticketPayload,
    validation,
    slotBookingPayload,
    subcategoryOptions = [],
    escalation,
    bypassedCombination,
    ticketData = [],
    escalateTicket,
    onSlotBookingFailed,
    customerData = {},
    onProductChangeComplete,
    slotNotCateredResolution = null,
    coachSlotSelection = null,
    onSlotNotCateredComplete
}) => {
    const { createTicket, submitProductChange, ticketLoading, ticketResponse } = useCreateTicket();
    // Same store the address box reads — the persist below needs the whole selected address, not the
    // postal-only copy that goes to /product-change.
    const { selectedAddress } = useAddressStore();
    const { updateAssignedData, updateLoading } = useAssignedStore();
    const [slotTime, setSlotTime] = useState(null);
    // ticketLoading briefly flips back to false between the two createTicket calls, so the button
    // re-enables mid-flow; this guard stops a double click from raising a second return ticket.
    const submittingRef = useRef(false);
    // Survives a failed attempt so a retry resumes at the step that failed instead of re-raising
    // the return ticket (which the backend would reject as a duplicate).
    const returnTicketRef = useRef({ raised: false, id: null });
    // slotTime of a coach slot already booked on a previous attempt. Survives a failed attempt so a
    // retry resumes at the ticket step instead of booking the customer a second call. Picking a
    // different slot changes the key, so a deliberate re-pick still books.
    const bookedCoachSlotRef = useRef(null);

    useEffect(() => {
        const slotTime = _.get(ticketResponse, ['medical_slot_time'], null);
        const time = slotTime ? getFormattedDate(slotTime) : slotTime;
        setSlotTime(time);
    }, [ticketResponse]);

    const onCreateTicket = async () => {
        if (submittingRef.current) return;
        submittingRef.current = true;
        let completedFlow = null;
        try {
            completedFlow = (await submitTicket()) || null;
        } finally {
            // Deliberately left armed on a completed flow. Its ticket is closed the moment it
            // is raised, and the service's duplicate guard only matches open/pending tickets, so a
            // second submit would sail through and raise another ticket against the same order.
            if (!completedFlow) submittingRef.current = false;
        }

        if (completedFlow === COMPLETED_FLOW.PRODUCT_CHANGE) onProductChangeComplete?.();
        if (completedFlow === COMPLETED_FLOW.SLOT_NOT_CATERED) onSlotNotCateredComplete?.();
    };

    const submitTicket = async () => {
        const payload = { ...ticketPayload };

        // Check if subcategory has slot configuration
        const currentSubcat = subcategoryOptions.find(
            (s) => s.value === ticketPayload.sub_category
        );

        const hasSlot = currentSubcat?.has_slot;
        const slotType = currentSubcat?.slot_type;

        // Check if inline transfer toggle is visible
        const isInlineTransferVisible =
            hasSlot &&
            slotType === SLOT_TYPES.MEDICAL_ESCALATION &&
            !noSlotInMedicalEscalation(ticketPayload.sub_category);

        const isInlineTransfer =
            ticketPayload.inline_transfer === true && isInlineTransferVisible;

        if (isInlineTransferVisible && !isInlineTransfer) {
            if (
                !ticketPayload.inline_input_comment ||
                ticketPayload.inline_input_comment.trim().length < 1
            ) {
                toast.error('Please add reason for slot booking');
                return;
            }
        }

        if (!isInlineTransfer) {
            if (hasSlot && slotType) {
                payload.slotBooking = slotBookingPayload;

                const slotTime = _.get(payload, ['slotBooking', 'slotTime'], false);

                if (slotTime && moment(slotTime).isBefore(moment())) {
                    toast.error('This slot has passed. Please select new slot.');
                    return;
                }

                payload.slotType = slotType;
            }

            if (
                hasSlot &&
                !noSlotInMedicalEscalation(ticketPayload.sub_category)
            ) {
                if (!slotBookingPayload.slotTime) {
                    toast.error('Please select a slot for this ticket');
                    return;
                }
            }
        }

        if (payload.category === HAIR_COACH_ISSUES && payload.sub_category === SLOT_NOT_CATERED) {
            return await submitSlotNotCateredTicket(payload);
        }

        const isProductChange = payload.sub_category === CATEGORY.PRODUCT_CHANGE;
        let returnTicketId = null;
        let replacementSummary = null;

        // The ticket service rejects a missing assignment, so check what is actually being sent
        // rather than what the subcategory has configured — the agent can pick one by hand.
        if (isProductChange && !payload.current_assignment) {
            toast.error('Select an assignee for this ticket');
            return;
        }

        // The order goes FIRST. A rejection here — an undelivered parent order, a restricted
        // geography — used to surface only after both tickets already existed, leaving the agent to
        // clean up tickets for a replacement that was never placed. Nothing is raised until the
        // replacement is real.
        if (isProductChange) {
            const placement = await placeReplacementOrder(payload);
            if (!placement.placed) return;
            replacementSummary = placement.replacementDetails;
        }

        if (isProductChange && payload.reverse_pickup_required) {
            if (!returnTicketRef.current.raised) {
                returnTicketRef.current = await createReturnTicket(payload);
            }
            if (!returnTicketRef.current.raised) return;
            returnTicketId = returnTicketRef.current.id;
        }

        // The backend used to add this line itself, back when it was given the return ticket id. It
        // never sees one now, so the id is appended here to keep the closing note complete.
        if (replacementSummary && returnTicketId) {
            replacementSummary = `${replacementSummary}\r\nProduct return ticket: ${returnTicketId}`;
        }

        // products live on the return ticket, so the product change ticket must not carry them —
        // /v2/create-ticket raises a refund for any products it is given. replacement_address is
        // dropped too: it belongs to /product-change, and the ticket has no use for a second address.
        const result = await createTicket(isProductChange
            ? _.omit(withReplacementSummary(payload, replacementSummary), ['products', 'total_refund_price', 'return_ticket', 'replacement_address'])
            : payload);
        if (result && !result.success && result.status === 422) {
            onSlotBookingFailed?.(result.message);
            return;
        }

        // A duplicate comes back as { status: 409, duplicate: true } inside a 200 and carries no
        // _id, so this ticket was NOT created — but the replacement order above already was. Say so
        // plainly: a retry would place a second order against the ticket that already exists.
        if (isProductChange && _.get(result, ['data', 'duplicate'], false)) {
            const message = _.get(result, ['data', 'message'], 'An open product change ticket already exists for this order');
            toast.warn(`${message}. The replacement order has already been created — do not retry.`);
            return;
        }

        // Only a placed replacement closes the ticket. A return-only product change leaves it open
        // for the agent, exactly as before.
        if (result?.success && isProductChange && replacementSummary) {
            await closeProductChangeTicket(payload, result.data, replacementSummary);

            // Reported so the caller can shut the window: the replacement order is placed and its
            // ticket closed, so there is nothing left to submit here.
            return COMPLETED_FLOW.PRODUCT_CHANGE;
        }
    };

    // Hair Coach Issues -> Slot not Catered resolves itself in one flow: the concern was either
    // already handled on the call, or a coach slot is booked for the customer. Either way nothing is
    // left to work on, so the ticket is raised and closed back to back — which is the entire point
    // of the subcategory, whose tickets used to sit Open with no action left on them.
    //
    // The slot is booked FIRST, mirroring the product change above: a booking the slot service
    // rejects must not leave behind an open ticket promising a call that was never scheduled.
    const submitSlotNotCateredTicket = async (payload) => {
        const needsCoach = slotNotCateredResolution === SLOT_NOT_CATERED_RESOLUTION.NEEDS_COACH;
        let closingNote = SLOT_NOT_CATERED_CLOSING_NOTE[SLOT_NOT_CATERED_RESOLUTION.RESOLVED];

        // A slot booked on an earlier attempt survives the agent switching to "resolved" — the call
        // is really on the coach's calendar, so the closing note has to say so rather than leave it
        // as an appointment nobody can trace back to this ticket.
        if (!needsCoach && bookedCoachSlotRef.current) {
            closingNote = `${closingNote} A coach slot was already booked for ${getFormattedDate(bookedCoachSlotRef.current)} during this interaction.`;
        }

        if (needsCoach) {
            const slot = _.get(coachSlotSelection, ['slot'], null);
            if (!slot) {
                toast.error('Please select a slot for the coach call');
                return;
            }

            // The panel can sit open long enough for the chosen slot to lapse, exactly as the
            // medical-escalation path guards against above.
            if (moment(slot.slotTime).isBefore(moment())) {
                toast.error('This slot has passed. Please select new slot.');
                return;
            }

            if (bookedCoachSlotRef.current !== slot.slotTime) {
                const booked = await bookCoachSlot(payload, slot);
                if (!booked) return;
                bookedCoachSlotRef.current = slot.slotTime;
            }

            closingNote = SLOT_NOT_CATERED_CLOSING_NOTE[SLOT_NOT_CATERED_RESOLUTION.NEEDS_COACH](
                getFormattedDate(slot.slotTime)
            );
        }

        const result = await createTicket(payload);
        if (!result?.success) return;

        // A duplicate comes back as { status: 409, duplicate: true } inside a 200 and carries no
        // _id, so no ticket was created here and there is nothing of ours to close. The slot, if it
        // was booked, still stands — the customer gets their call either way.
        const ticketId = _.get(result, ['data', '_id'], null);
        if (!ticketId) {
            const message = _.get(result, ['data', 'message'], 'An open ticket already exists for this order');
            toast.warn(`${message}. This ticket was not created, so nothing was closed.`);
            return;
        }

        const closed = await closeSlotNotCateredTicket(payload, ticketId, closingNote);
        if (!closed) {
            // The ticket exists but is still Open — the exact backlog this flow exists to prevent —
            // so it cannot be reported as done. Resubmitting will not help: the service's duplicate
            // guard rejects the second create, so the agent is pointed at My Tickets instead.
            toast.error(`Ticket ${ticketId} was created but could not be closed. Please close it from My Tickets.`);
            return;
        }

        return COMPLETED_FLOW.SLOT_NOT_CATERED;
    };

    // Same endpoint and payload the Slot-Booking modal sends (ConsultDoctorCanvas), against the same
    // slot list, so a booking made from this flow is indistinguishable from one made there.
    const bookCoachSlot = async (payload, slot) => {
        try {
            await slotApi({
                case_id: _.get(payload, ['case_id'], null),
                slot_id: slot.id,
                slots: slot.slots,
                order_related: false,
                is_rescheduling: true,
                session_id: _.get(coachSlotSelection, ['sessionId'], ''),
                from_crm: true,
            });
            return true;
        } catch (error) {
            const fallbackError = _.get(error, ['message'], 'Failed to book the coach slot');
            const errorMessage = _.get(error, ['response', 'data', 'message'], fallbackError);
            toast.error(_.get(error, ['response', 'data', 'message', 'message'], errorMessage));
            return false;
        }
    };

    // Mirrors closeProductChangeTicket: UpdateTicket gates closing on callType and is_valid, so both
    // belong here even though status_code does the move. The comment is internal on purpose — a
    // customer-facing one emits ticket_customer_facing_comment_added, one of the notifications this
    // subcategory deliberately suppresses for CRM-raised tickets.
    //
    // Returns whether the close actually landed. updateAssignedData swallows its own errors and
    // always resolves, so the store's error field is the only signal that the ticket did not close —
    // without checking it, a failed close would be reported to the agent as a finished flow.
    const closeSlotNotCateredTicket = async (payload, ticketId, closingNote) => {
        const actionTaker = _.get(payload, 'action_taker', null) || _.get(payload, ['comment', 'commented_by'], null);

        await updateAssignedData(ticketId, {
            comment: {
                type: HAIR_COACH_ISSUES,
                description: closingNote,
                commented_by: actionTaker,
                comment_type: 'internal',
                visibility_type: 'internal',
            },
            action_taker: actionTaker,
            status_code: TICKET_STATUS_CLOSED,
            is_valid: true,
            callType: 'unanswered',
        });

        return !useAssignedStore.getState().assignedTicketError;
    };

    // The return itself rides on this ticket: /v2/create-ticket raises the refund for any
    // subcategory configured with refund_request once products are attached.
    const createReturnTicket = async (payload) => {
        // replacement_address belongs to /product-change only. The return ticket already carries
        // the PICKUP address in `address`; a second address on the same ticket is exactly the mix-up
        // this flow exists to end, and the ticketing service has no use for either field.
        const { return_ticket: returnTicket, replacement_products, reverse_pickup_required, payment_option, replacement_address, ...rest } = payload;
        if (!returnTicket) {
            toast.error('The return ticket could not be resolved for this order');
            return { raised: false, id: null };
        }

        const subject = String(_.get(rest, ['provider_meta', 'subject'], '')).split(':')[0];
        const result = await createTicket({
            ...rest,
            sub_category: returnTicket.sub_category,
            sub_category_id: returnTicket.sub_category_id,
            current_assignment: returnTicket.current_assignment,
            provider_meta: { ...rest.provider_meta, subject: `${subject}: ${rest.category} --> ${returnTicket.sub_category}` },
            comment: { ...rest.comment, description: `Product return raised from product change.\r\n${_.get(rest, ['comment', 'description'], '')}` },
        });

        if (!result?.success) return { raised: false, id: null };

        // A duplicate is by definition an OPEN return ticket for this same order and subcategory, so
        // the return already exists — carry on with its id (the service embeds it in the message)
        // instead of stranding the product change behind a guard it can never get past.
        if (_.get(result, ['data', 'duplicate'])) {
            const message = _.get(result, ['data', 'message'], '');
            toast.warn(message || 'A return ticket already exists for this order — reusing it');
            return { raised: true, id: _.get(String(message).match(/\[([a-f\d]{24})\]/i), [1], null) };
        }
        return { raised: true, id: _.get(result, ['data', '_id'], null) };
    };

    // Runs before any ticket exists, so /product-change is called without a ticketId and closes
    // nothing — this file closes the ticket itself once it has one.
    const placeReplacementOrder = async (payload) => {
        const newProducts = _.get(payload, ['replacement_products'], []);
        // A product change with nothing to send back is a return on its own, which is valid: fall
        // through and let the tickets be raised as before rather than inventing a new validation.
        if (!newProducts.length) return { placed: true, replacementDetails: null };

        const shippingAddress = _.get(payload, ['replacement_address'], null);

        // Persist ONLY a freshly-entered (local) address, mirroring sendLink in the prepaid link flow:
        // PREPAID never reads the address we send, it resolves the customer's newest saved row instead,
        // both for its pincode gate and again when the paid order is placed. An already-saved address
        // is left alone — re-posting it would send a bare phone and fail /address validation.
        if (payload.payment_option === PAYMENT_OPTIONS.PREPAID && String(_.get(selectedAddress, '_id', '')).startsWith('local_')) {
            const { _id, ...addressData } = selectedAddress;
            try {
                await addCustomerAddress(_.get(customerData, ['user', 'id'], null), addressData);
            } catch (error) {
                toast.error(_.get(error, ['response', 'data', 'message'], 'Failed to save the replacement delivery address'));
                return { placed: false, replacementDetails: null };
            }
        }

        const result = await submitProductChange({
            identifierId: _.get(payload, ['source_meta', 'order_id']),
            newProducts,
            paymentOption: payload.payment_option,
            // Still sent: COD honours it as an explicit override that does not depend on recency.
            shippingAddress,
            reason: payload.comment?.description,
        });

        // submitProductChange already surfaces the backend's own message, so a rejected order just
        // stops here without a second toast. No ticket has been created yet — the point of the reorder.
        if (!result?.success) return { placed: false, replacementDetails: null };

        return { placed: true, replacementDetails: _.get(result, ['data', 'replacement_details'], null) };
    };

    // Mirrors the body the backend sent when it still closed this ticket itself. UpdateTicket gates
    // closing on callType and is_valid, so both belong here even though status_code does the move.
    const closeProductChangeTicket = async (payload, ticket, replacementSummary) => {
        const ticketId = _.get(ticket, ['_id'], null);
        if (!ticketId) return;

        const actionTaker = _.get(payload, 'action_taker', null) || _.get(payload, ['comment', 'commented_by'], null);

        await updateAssignedData(ticketId, {
            comment: {
                type: 'delivery_related_issues',
                description: replacementSummary,
                commented_by: actionTaker,
            },
            action_taker: actionTaker,
            status_code: TICKET_STATUS_CLOSED,
            is_valid: true,
            callType: 'unanswered',
        });
    };

    const onEscalateExisting = async () => {
        try {
            // Find all matching tickets (same category/subcategory within 15 days)
            const { category, sub_category } = bypassedCombination;
            const isMedicalEscalation = category === CATEGORY.MEDICAL_ESCALATIONS;

            const matchingTickets = ticketData.filter((ticket) => {
                const isWithin15Days = moment(ticket.updatedAt).isAfter(
                    moment().subtract(15, 'days').startOf('day')
                );
                if (!isWithin15Days) return false;

                if (isMedicalEscalation) {
                    return ticket.category === CATEGORY.MEDICAL_ESCALATIONS &&
                           (!sub_category || ticket.sub_category === sub_category);
                } else {
                    return ticket.category === category &&
                           ticket.sub_category === sub_category;
                }
            });

            if (matchingTickets.length === 0) {
                toast.error('Could not find the ticket to escalate');
                return;
            }

            // Priority 1: Find ticket with Open status
            let existingTicket = matchingTickets.find((ticket) => ticket.status === 'Open');

            // Priority 2: If no Open ticket, get most recently updated
            if (!existingTicket) {
                existingTicket = matchingTickets.sort((a, b) =>
                    moment(b.updatedAt).diff(moment(a.updatedAt))
                )[0];
            }

            // Build payload only with required changes
            const escalatePayload = {
                escalation: true,
            };

            // Only add status fields if ticket is not already Open
            if (existingTicket.status !== 'Open') {
                escalatePayload.status = 'Open';
                escalatePayload.status_code = 1;
            }

            // Call the Zustand store action and wait for completion
            await updateAssignedData(existingTicket._id, escalatePayload);
        } catch (error) {
            const errorMessage = _.get(
                error,
                ['response', 'data', 'message'],
                error.message || 'Failed to escalate the ticket'
            );
            toast.error(errorMessage);
        }
    };

    const failingValidations = Object.entries(validation).filter(([, value]) => !value).map(([key]) => key);
    const isButtonDisabled = ticketLoading ||
        (validation && _.size(Object.values(validation)) === 0) ||
        _.size(failingValidations);

    // Debug: log validation state when button is disabled
    if (isButtonDisabled && !ticketLoading) {
        console.log('[CreateTicket] Button disabled. Validation state:', JSON.stringify(validation), 'Failing:', failingValidations);
    }

    return (
        <div className="bottom-0 border border-1 p-3">
            {escalation ? (
                <div className="d-flex justify-flex-between align-items-center gap-4">
                    <Button
                        onClick={escalateTicket}
                    >
                        Escalate
                    </Button>
                </div>
            ) : (
                <div className={bypassedCombination ? 'd-flex flex-column gap-3' : 'd-flex justify-flex-between align-items-center gap-4'}>
                    <div className="d-flex gap-2">
                        <Button
                            disabled={isButtonDisabled}
                            onClick={onCreateTicket}
                        >
                            Create Ticket
                        </Button>

                        {/* Show "Escalate Existing" button when user has bypassed escalation */}
                        {bypassedCombination && (
                            <Button
                                onClick={onEscalateExisting}
                                loading={updateLoading}
                            >
                                Escalate Existing
                            </Button>
                        )}
                    </div>

                    {/* Show slot status for subcategories that have slots */}
                    {(() => {
                        const currentSubcat = subcategoryOptions.find(
                            (s) => s.value === ticketPayload.sub_category
                        );

                        const hasSlot = currentSubcat?.has_slot;
                        const slotType = currentSubcat?.slot_type;

                        return (
                            hasSlot && (
                                <Space.Compact block className={bypassedCombination ? 'my-0' : 'my-3'}>
                  <span
                      style={{
                          background: '#FAFAFA',
                          border: '#D9D9D9',
                          width: '30%',
                      }}
                      className="border border-1 border-end-0 d-flex justify-conent-center align-items-center rounded-lg rounded-start px-2"
                  >
                    <Text>
                      {slotType === SLOT_TYPES.MEDICAL_ESCALATION &&
                      ticketPayload.inline_transfer
                          ? 'Inline Transfer'
                          : 'Slot Booked'}
                    </Text>
                  </span>

                                    <Input
                                        style={{ width: '70%' }}
                                        value={
                                            slotType === SLOT_TYPES.MEDICAL_ESCALATION &&
                                            ticketPayload.inline_transfer
                                                ? 'Enabled - Direct Assignment'
                                                : slotTime
                                        }
                                    />
                                </Space.Compact>
                            )
                        );
                    })()}
                </div>
            )}
        </div>
    );
};

export default CreateTicketFooter;
